import type { Server } from "@cloudserver/contracts";
import { useTranslation } from "react-i18next";
import { useServerAction } from "../../api/servers.js";
import { DangerDialog } from "../../ui/confirm.js";
import { ErrorMessage } from "../../ui/feedback.js";
import { useToast } from "../../ui/toast.js";

/** Power actions that interrupt a running server and therefore ask first. */
export type DisruptiveAction = "shutdown" | "power-off" | "reboot" | "reset";

/**
 * Confirmation of a power action: says what it does to the operating system
 * and that a stopped server is still billed (SPEC §8.6).
 */
export function PowerDialog({
  projectId,
  server,
  action,
  onClose,
  onStarted,
  onConfirmed,
}: {
  projectId: string;
  server: Server;
  action: DisruptiveAction | null;
  onClose: () => void;
  onStarted?: (operationId: string) => void;
  /**
   * The person confirmed, before the request goes: what has to be ready
   * before the server reacts (the console expecting its restart).
   */
  onConfirmed?: (action: DisruptiveAction) => void;
}) {
  const { t } = useTranslation();
  const run = useServerAction(projectId);
  const toast = useToast();
  const key = action ?? "shutdown";
  const close = () => {
    run.reset();
    onClose();
  };
  return (
    <DangerDialog
      open={action !== null}
      onOpenChange={(open) => !open && close()}
      title={t(`servers.power.${key}.confirmTitle`, { name: server.name })}
      consequences={[
        t(`servers.power.${key}.consequence`),
        ...(key === "shutdown" || key === "power-off" ? [t("servers.power.stillBilled")] : []),
      ]}
      actionLabel={t(`servers.power.${key}.action`)}
      busy={run.isPending}
      onConfirm={() => {
        if (!action) return;
        onConfirmed?.(action);
        run.mutate(
          { serverId: server.id, action },
          {
            onSuccess: (result) => {
              toast({
                tone: "info",
                title: t(`servers.power.${key}.started`, { name: server.name }),
              });
              onStarted?.(result.operation.id);
              close();
            },
          },
        );
      }}
    >
      <ErrorMessage error={run.error} />
    </DangerDialog>
  );
}
